import { Game } from "@/data/Game";
import { Environment } from "@/data/Environment";
import { Integration } from "@/data/Integration";
import { Language } from "@/data/Language";
import { localStorageSet } from "./Utils";

export class TestingParameters {

    private _game: Game;
    private _environment: Environment;
    private _integration: Integration;
    private _language: Language;
    private _currency: string;
    private _playMode: string;
    private _playerId: string;
    private _token: string;
    private _localToken: string;
    private _delay: boolean;
    private _turbo: boolean;
    private _netPosition: boolean;
    private _krakenPlayerId: string;
    private _prototypeUrl: string;
    private _balance: number;
    private _forceBalance: boolean;
    private _forceCustomError: boolean;
    private _forceDemo: boolean;
    private _showTheoreticalRTP: boolean;
    private _homeButtonEnabled: boolean;
    private _depositEnabled: boolean;
    private _buyBonusEnabled: boolean;
    private _historyEnabled: boolean;
    private _autoplay: boolean;
    private _queryParams: string;

    private _freeRoundStake: number;
    private _freeRoundTotal: number;

    private _autoCompleteGameRoundId: number;

    constructor() {
        this.reset();
        this._environment = null;
        this._integration = null;
        this._language = null;
        this._currency = "";
        this._queryParams = "&testParam=example"
        this._playMode = "";
        this._currency = "";
        this._playerId = "";
        this._token = this.createGuid();
    }

    public reset(): void {
        this._game = null;
        this._delay = false;
        this._turbo = true;
        this._balance = 50000;
        this._forceBalance = false;
        this._forceCustomError = false;
        this._netPosition = true;
        this._autoplay = true;
        this._localToken = null;
        this._prototypeUrl = null;
        this._showTheoreticalRTP = true;
        this._homeButtonEnabled = true;
        this._depositEnabled = true;
        this._buyBonusEnabled = true;
        this._historyEnabled = true;
        this._freeRoundStake = 1;
        this._freeRoundTotal = 5;
        this._autoCompleteGameRoundId = 0;
    }

    public resetPlayerId(): void {
        this.token = this.createGuid();
        this.playerId = this.token;
    }

    get launchAddress(): string {
        const address = new URL(this.environment.serverAddress);
        const params = new URLSearchParams(this.launchParameters).toString();
        address.pathname = address.pathname + "kraken/launch/" + this.integration.operator;
        address.search = params;
        return address.toString();
    }

    get localUrl(): string {
        const params = new URLSearchParams(this.localParameters).toString();
        return this.prototypeUrl + "?" + params;
    }

    get launchParameters(): Record<string, string> {
        if (this.game != null) {

            let token: string = this.token + "|playerId:" + this.playerId;

            if (this.forceBalance) {
                token += ",initialBalance:" + this.balance;
            }
            if (this.forceCustomError) {
                token += ",wagerMode:CUSTOM_ERROR";
            }

            token += ",currency:" + this.currency;
            token += ",randomDelay:" + this.delay;
            token += "|";

            return {
                gameId: this.game.selectedConfig.id,
                version: this.game.version,
                playerId: this.playerId,
                currency: this.currency,
                language: this.language.id,
                operator: this.integration.operator,
                playMode: this.playMode,
                operatorToken: token,
                type: "noredirect",
                serverAddress: this.environment.serverAddress
            }
        } else {
            return {}
        }
    }

    get localParameters(): Record<string, string> {
        if (this.localToken != null) {
            return {
                serverAddress: this.environment.serverAddress,
                serverPath: this._game.serverPath,
                token: this.localToken,
                operator: this.integration.operator,
                language: this.language.id,
                currency: this.currency,
                gameId: this.game.selectedConfig.id,
                playerId: this.playerId,
                testConfig: "ksg",
                playMode: this.playMode
            }
        } else {
            return {}
        }
    }

    get autoplay(): boolean {
        return this._autoplay;
    }

    set autoplay(value: boolean) {
        this._autoplay = value;
    }

    get showTheoreticalRTP(): boolean {
        return this._showTheoreticalRTP;
    }

    set showTheoreticalRTP(value: boolean) {
        this._showTheoreticalRTP = value;
    }

    get homeButtonEnabled(): boolean {
        return this._homeButtonEnabled;
    }

    set homeButtonEnabled(value: boolean) {
        this._homeButtonEnabled = value;
    }

    get depositEnabled(): boolean {
        return this._depositEnabled;
    }

    set depositEnabled(value: boolean) {
        this._depositEnabled = value;
    }

    get buyBonusEnabled(): boolean {
        return this._buyBonusEnabled;
    }

    set buyBonusEnabled(value: boolean) {
        this._buyBonusEnabled = value;
    }

    get historyEnabled(): boolean {
        return this._historyEnabled;
    }

    set historyEnabled(value: boolean) {
        this._historyEnabled = value;
    }

    get queryParams(): string {
        return this._queryParams;
    }

    set queryParams(value: string) {
        this._queryParams = value;
    }

    get game(): Game {
        return this._game;
    }

    set game(value: Game) {
        this._game = value;
    }

    get environment(): Environment {
        return this._environment;
    }

    set environment(value: Environment) {
        this._environment = value;
    }

    get integration(): Integration {
        return this._integration;
    }

    set integration(value: Integration) {
        this._integration = value;
    }

    get token(): string {
        return this._token;
    }

    set token(value: string) {
        this._token = value;
    }

    get playerId(): string {
        return this._playerId;
    }

    set playerId(value: string) {
        localStorageSet("player_id", value);
        this._playerId = value;
    }

    get playMode(): string {
        return this._playMode;
    }

    set playMode(value: string) {
        this._playMode = value;
    }

    get currency(): string {
        return this._currency;
    }

    set currency(value: string) {
        localStorageSet("player_currency", value);
        this._currency = value;
    }

    get language(): Language {
        return this._language;
    }

    set language(value: Language) {
        this._language = value;
    }

    get prototypeUrl(): string {
        return this._prototypeUrl;
    }

    set prototypeUrl(value: string) {
        this._prototypeUrl = value;
    }

    get netPosition(): boolean {
        return this._netPosition;
    }

    set netPosition(value: boolean) {
        this._netPosition = value;
    }

    get turbo(): boolean {
        return this._turbo;
    }

    set turbo(value: boolean) {
        this._turbo = value;
    }

    set balance(value: number) {
        this._balance = value;
    }

    get balance(): number {
        return this._balance;
    }

    set forceBalance(value: boolean) {
        this._forceBalance = value;
    }

    get forceBalance(): boolean {
        return this._forceBalance;
    }

    get forceCustomError(): boolean {
        return this._forceCustomError;
    }

    set forceCustomError(value: boolean) {
        this._forceCustomError = value;
    } 

    set forceDemo(value: boolean) {
        this._forceDemo = value;
    }

    get forceDemo(): boolean {
        return this._forceDemo;
    }

    get delay(): boolean {
        return this._delay;
    }

    set delay(value: boolean) {
        this._delay = value;
    }

    get krakenPlayerId(): string {
        return this._krakenPlayerId;
    }

    set krakenPlayerId(value: string) {
        this._krakenPlayerId = value;
    }

    get localToken(): string {
        return this._localToken;
    }

    set localToken(value: string) {
        this._localToken = value;
    }

    set freeRoundStake(value: number) {
        this._freeRoundStake = value;
    }

    get freeRoundStake(): number {
        return this._freeRoundStake;
    }

    set freeRoundTotal(value: number) {
        this._freeRoundTotal = value;
    }

    get freeRoundTotal(): number {
        return this._freeRoundTotal;
    }

    get autoCompleteGameRoundId(): number {
        return this._autoCompleteGameRoundId;
    }

    set autoCompleteGameRoundId(value: number) {
        this._autoCompleteGameRoundId = value;
    }

    private createGuid(): string {
        let d = new Date().getTime();
        const guid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
        });
        return guid;
    }
}
